<template>
  <div v-if="focusSpotlightRect" class="focus-tutorial-overlay" @click="dismiss">
    <div
      class="focus-tutorial-spotlight"
      :style="{
        top: focusSpotlightRect.top + 'px',
        left: focusSpotlightRect.left + 'px',
        width: focusSpotlightRect.width + 'px',
        height: focusSpotlightRect.height + 'px',
      }"
    ></div>
    <div
      v-if="focusMessage && focusTooltipPosition"
      class="focus-url-tooltip"
      :class="{ 'focus-url-tooltip-left': focusTooltipPosition.onLeft }"
      :style="{
        left: focusTooltipPosition.left + 'px',
        top: focusTooltipPosition.top + 'px',
      }"
      @click.stop
    >
      <div class="focus-url-tooltip-arrow"></div>
      <div class="focus-url-tooltip-content">
        <i class="fas fa-info-circle me-2 text-primary"></i>
        <span class="flex-grow-1">{{ focusMessage }}</span>
        <button
          type="button"
          class="focus-url-tooltip-close"
          aria-label="Close"
          @click="dismiss"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Overlay tipo tutorial para todo el sistema.
 * Se activa con query params: ?focusON=id&message=Texto
 * - focusON: valor de data-focus-id del elemento o selector CSS (#id / .clase)
 * - message: texto del tooltip (opcional)
 * Cualquier página puede usar data-focus-id en elementos para ser enfocables desde la URL.
 */
export default {
  name: "FocusTutorialOverlay",
  data() {
    return {
      focusMessage: null,
      focusTooltipPosition: null,
      focusSpotlightRect: null,
      focusRetryTimer: null,
      focusTrackTimer: null,
    };
  },
  watch: {
    "$route.query": {
      handler(query) {
        if (query.focusON || query.focusOn) {
          this.$nextTick(() => this.startFocusAttempts());
        } else {
          this.clear();
        }
      },
      immediate: true,
    },
  },
  methods: {
    startFocusAttempts() {
      this.stopFocusAttempts();
      let attempts = 0;
      const maxAttempts = 24;
      const initialDelayMs = 1000;
      const tryApply = () => {
        const applied = this.applyFocus();
        attempts += 1;
        if (applied || attempts >= maxAttempts) {
          this.stopFocusAttempts();
          return;
        }
        this.focusRetryTimer = setTimeout(tryApply, 250);
      };
      this.focusRetryTimer = setTimeout(tryApply, initialDelayMs);
    },
    stopFocusAttempts() {
      if (this.focusRetryTimer) {
        clearTimeout(this.focusRetryTimer);
        this.focusRetryTimer = null;
      }
    },
    startTrackingElement(el) {
      if (!el) return;
      this.stopTrackingElement();
      let ticks = 0;
      const maxTicks = 24; // ~3s
      this.focusTrackTimer = setInterval(() => {
        ticks += 1;
        if (!el.isConnected || ticks > maxTicks) {
          this.stopTrackingElement();
          return;
        }
        const rect = el.getBoundingClientRect();
        const padding = 8;
        this.focusSpotlightRect = {
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
        };
        if (this.focusMessage) {
          const gap = 12;
          const tooltipMaxWidth = 320;
          const margin = 16;
          const tooltipHeightEstimate = 56;
          let left = rect.right + gap;
          let onLeft = false;
          if (left + tooltipMaxWidth > window.innerWidth - margin) {
            left = Math.max(margin, rect.left - tooltipMaxWidth - gap);
            onLeft = true;
          } else if (left < margin) {
            left = margin;
          }
          let top = rect.top + rect.height / 2 - tooltipHeightEstimate / 2;
          top = Math.max(margin, Math.min(window.innerHeight - tooltipHeightEstimate - margin, top));
          this.focusTooltipPosition = { left, top, onLeft };
        }
      }, 125);
    },
    stopTrackingElement() {
      if (this.focusTrackTimer) {
        clearInterval(this.focusTrackTimer);
        this.focusTrackTimer = null;
      }
    },
    applyFocus() {
      const focusON = this.$route.query.focusON || this.$route.query.focusOn;
      const message = this.$route.query.message;
      if (!focusON) return false;

      let el = null;
      if (focusON.startsWith("#") || focusON.startsWith(".")) {
        el = document.querySelector(focusON);
      } else {
        el = document.querySelector(`[data-focus-id="${focusON}"]`);
      }
      if (el) {
        const isElementInViewport = (rect) =>
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= window.innerHeight &&
          rect.right <= window.innerWidth;

        let rect = el.getBoundingClientRect();
        if (!isElementInViewport(rect)) {
          const targetTop = window.scrollY + rect.top - window.innerHeight / 2 + rect.height / 2;
          window.scrollTo({
            top: Math.max(0, targetTop),
            behavior: "auto",
          });
          rect = el.getBoundingClientRect();
        }

        const padding = 8;
        this.focusSpotlightRect = {
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
        };
        if (message) {
          try {
            this.focusMessage = decodeURIComponent(message);
          } catch (e) {
            this.focusMessage = message;
          }
          const gap = 12;
          const tooltipMaxWidth = 320;
          const margin = 16;
          const tooltipHeightEstimate = 56;
          let left = rect.right + gap;
          let onLeft = false;
          if (left + tooltipMaxWidth > window.innerWidth - margin) {
            left = Math.max(margin, rect.left - tooltipMaxWidth - gap);
            onLeft = true;
          } else if (left < margin) {
            left = margin;
          }
          let top = rect.top + rect.height / 2 - tooltipHeightEstimate / 2;
          top = Math.max(margin, Math.min(window.innerHeight - tooltipHeightEstimate - margin, top));
          this.focusTooltipPosition = {
            left,
            top,
            onLeft,
          };
        }
        this.startTrackingElement(el);
        return true;
      }
      return false;
    },
    dismiss() {
      this.clear();
      const query = { ...this.$route.query };
      delete query.focusON;
      delete query.focusOn;
      delete query.message;
      this.$router.replace({ path: this.$route.path, query }).catch(() => {});
    },
    clear() {
      this.stopFocusAttempts();
      this.stopTrackingElement();
      this.focusMessage = null;
      this.focusTooltipPosition = null;
      this.focusSpotlightRect = null;
    },
  },
  beforeDestroy() {
    this.stopFocusAttempts();
    this.stopTrackingElement();
  },
};
</script>

<style scoped>
.focus-tutorial-overlay {
  position: fixed;
  inset: 0;
  z-index: 9998;
  background: transparent;
  cursor: pointer;
}

.focus-tutorial-spotlight {
  position: fixed;
  pointer-events: none;
  border-radius: 10px;
  box-shadow:
    0 0 0 3px rgba(13, 110, 253, 0.85),
    0 0 0 9999px rgba(0, 0, 0, 0.78);
  transition: box-shadow 0.25s ease;
}

.focus-url-tooltip {
  position: fixed;
  z-index: 9999;
  max-width: 320px;
  pointer-events: auto;
  cursor: default;
}

.focus-url-tooltip-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 12px 14px;
  background: #fff;
  border-radius: 10px;
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(0, 0, 0, 0.08),
    0 0 48px rgba(13, 110, 253, 0.15);
  font-size: 0.9rem;
  line-height: 1.4;
  color: #333;
}

.focus-url-tooltip-arrow {
  position: absolute;
  left: -8px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-right: 8px solid #fff;
  filter: drop-shadow(-2px 0 2px rgba(0, 0, 0, 0.1));
}

.focus-url-tooltip-left .focus-url-tooltip-arrow {
  left: auto;
  right: -8px;
  border-right: none;
  border-left: 8px solid #fff;
  filter: drop-shadow(2px 0 2px rgba(0, 0, 0, 0.1));
}

.focus-url-tooltip-close {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border: none;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 6px;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
}

.focus-url-tooltip-close:hover {
  background: rgba(0, 0, 0, 0.1);
  color: #333;
}
</style>
